import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi, type DoctorAvailabilityCalendar } from '@/api';

const AVAILABILITY_KEY = (doctorId: string) => ['doctor', doctorId, 'availability'] as const;

async function fetchAvailabilityCalendar(
  doctorId: string,
  from: string,
  to: string
): Promise<DoctorAvailabilityCalendar> {
  return schedulingApi.getAvailabilityCalendar(doctorId, from, to);
}

export function useDoctorAvailabilityCalendar(doctorId: string, from: string, to: string) {
  return useQuery({
    queryKey: [...AVAILABILITY_KEY(doctorId), { from, to }] as const,
    queryFn: () => fetchAvailabilityCalendar(doctorId, from, to),
    enabled: !!doctorId && !!from && !!to,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
  });
}

export function useUpdateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      rules: Array<{
        dayOfWeek: number;
        isActive: boolean;
        startTime: string;
        endTime: string;
        duration?: number;
        interval?: number;
        priority?: number;
      }>;
      clearExisting?: boolean;
    }) => schedulingApi.updateAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor'] });
    },
  });
}

export function useBlockTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      type: string;
      startDate: string;
      endDate: string;
      reason?: string;
      message?: string;
    }) => schedulingApi.blockTime(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor'] });
    },
  });
}

export function useScheduleRules(doctorId: string) {
  return useQuery({
    queryKey: [...AVAILABILITY_KEY(doctorId), 'rules'] as const,
    queryFn: () => schedulingApi.getScheduleRules(doctorId),
    enabled: !!doctorId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
  });
}
